import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon
} from '@heroicons/react/24/outline';

const TripGps = () => {
  const [records, setRecords] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0, totalPages: 0, currentPage: 0, size: 10
  });
  
  const [trips, setTrips] = useState([]);
  const [events, setEvents] = useState([]);
  const [videos, setVideos] = useState([]);
  
  const [filters, setFilters] = useState({
    tripId: '',
    eventId: '',
    videoId: '',
    fromDtTm: '',
    toDtTm: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const navigate = useNavigate();

  useEffect(() => { fetchDropdownData(); }, []);
  useEffect(() => { fetchRecords(); }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchDropdownData = async () => {
    const tripsRes = await get('/api/trips/filter?size=100');
    if (tripsRes.data) setTrips(tripsRes.data.content || []);
    const eventsRes = await get('/api/events/filter?size=100');
    if (eventsRes.data) setEvents(eventsRes.data.content || []);
    const videosRes = await get('/api/videos/filter?size=100');
    if (videosRes.data) setVideos(videosRes.data.content || []);
  };

  const fetchRecords = async () => {
    const params = new URLSearchParams();
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.tripId) params.append('tripId', filters.tripId);
    if (filters.eventId) params.append('eventId', filters.eventId);
    if (filters.videoId) params.append('videoId', filters.videoId);
    if (filters.fromDtTm) params.append('fromDtTm', filters.fromDtTm);
    if (filters.toDtTm) params.append('toDtTm', filters.toDtTm);
    
    const endpoint = `/api/trip-gps/filter?${params.toString()}`;
    const { data } = await get(endpoint);
    if (data) {
      let filteredData = data.content || [];
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredData = filteredData.filter(r => 
          r.id?.toString().includes(term) ||
          r.tripId?.toString().includes(term) ||
          r.latitude?.toString().includes(term) ||
          r.longitude?.toString().includes(term)
        );
      }
      setRecords(filteredData);
      setPageInfo({
        totalElements: data.totalElements, totalPages: data.totalPages,
        currentPage: data.number || 0, size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this GPS record?')) {
      const { error } = await del(`/api/trip-gps/${id}`);
      if (!error) fetchRecords();
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords();
  };

  const clearFilters = () => {
    setFilters({ tripId: '', eventId: '', videoId: '', fromDtTm: '', toDtTm: '' });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const formatCoordinate = (value) => value?.toFixed(6) || '-';

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Trip ID', accessor: 'tripId' },
    { header: 'Event ID', accessor: (row) => row.eventId || '-' },
    { header: 'Video ID', accessor: (row) => row.videoId || '-' },
    { header: 'Timestamp', accessor: (row) => row.timestamp ? new Date(row.timestamp).toLocaleString() : '-' },
    { header: 'Latitude', accessor: (row) => formatCoordinate(row.latitude) },
    { header: 'Longitude', accessor: (row) => formatCoordinate(row.longitude) },
    { header: 'Speed', accessor: (row) => row.speed ? `${row.speed.toFixed(1)} km/h` : '-' },
    { header: 'Actions', accessor: (row) => (
      <div className="flex space-x-2">
        <button onClick={() => navigate(`/trip-gps/${row.id}`)} className="text-blue-600 hover:text-blue-900"><EyeIcon className="h-5 w-5" /></button>
        <button onClick={() => navigate(`/trip-gps/edit/${row.id}`)} className="text-green-600 hover:text-green-900"><PencilIcon className="h-5 w-5" /></button>
        <button onClick={() => handleDelete(row.id)} className="text-red-600 hover:text-red-900"><TrashIcon className="h-5 w-5" /></button>
      </div>
    )}
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Trip GPS Records</h1>
        <button onClick={() => navigate('/trip-gps/add')} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center">
          <PlusIcon className="h-5 w-5" /><span>Add Record</span>
        </button>
      </div>

      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by ID, trip ID..." className="input-field pl-10" />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Search</button>
          <button type="button" onClick={() => setShowFilters(!showFilters)} className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            <FunnelIcon className="h-5 w-5" /><span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.tripId || filters.eventId || filters.videoId || filters.fromDtTm || filters.toDtTm || searchTerm) && (
            <button type="button" onClick={clearFilters} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
              <XMarkIcon className="h-5 w-5" /><span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center"><FunnelIcon className="h-4 w-4 mr-1" />Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Trip</label>
              <select name="tripId" value={filters.tripId} onChange={handleFilterChange} className="input-field text-sm">
                <option value="">All Trips</option>
                {trips.map(t => <option key={t.id} value={t.id}>Trip #{t.id}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Event</label>
              <select name="eventId" value={filters.eventId} onChange={handleFilterChange} className="input-field text-sm">
                <option value="">All Events</option>
                {events.map(e => <option key={e.id} value={e.id}>Event #{e.id}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Video</label>
              <select name="videoId" value={filters.videoId} onChange={handleFilterChange} className="input-field text-sm">
                <option value="">All Videos</option>
                {videos.map(v => <option key={v.id} value={v.id}>Video #{v.id}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1 flex items-center"><CalendarIcon className="h-3 w-3 mr-1" />From Date</label>
              <input type="datetime-local" name="fromDtTm" value={filters.fromDtTm || ''} onChange={handleFilterChange} className="input-field text-sm" />
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1 flex items-center"><CalendarIcon className="h-3 w-3 mr-1" />To Date</label>
              <input type="datetime-local" name="toDtTm" value={filters.toDtTm || ''} onChange={handleFilterChange} className="input-field text-sm" />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick dates:</span>
            <button onClick={() => {
              const today = new Date(); const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
              setFilters(prev => ({ ...prev, fromDtTm: yesterday.toISOString().slice(0, 16), toDtTm: today.toISOString().slice(0, 16) }));
            }} className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200">Last 24 Hours</button>
            <button onClick={() => {
              const today = new Date(); const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
              setFilters(prev => ({ ...prev, fromDtTm: weekAgo.toISOString().slice(0, 16), toDtTm: today.toISOString().slice(0, 16) }));
            }} className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200">Last 7 Days</button>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-gray-600">
        Showing {records.length} of {pageInfo.totalElements} records
      </div>

      <DataTable columns={columns} data={records} pageInfo={pageInfo} onPageChange={(page) => setPageInfo({...pageInfo, currentPage: page})} />
    </div>
  );
};

export default TripGps;